import { twMerge } from 'tailwind-merge';
import clsx, { ClassValue } from 'clsx';

export const cn = (...inputs: ClassValue[]) => twMerge(clsx(inputs));

export const triggerDirectDownload = (
    downloadUrl: string,
    filename: string
): void => {
    const anchor = document.createElement('a');
    anchor.href = downloadUrl;
    anchor.download = filename;
    anchor.rel = 'noopener';
    document.body.append(anchor);
    anchor.click();
    setTimeout(() => {
        anchor.remove();
    }, 0);
};

export const generateRandomString = (length: number) => {
    const charset =
        'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    const values = crypto.getRandomValues(new Uint32Array(length));

    for (let index = 0; index < length; index += 1) {
        result += charset[values[index] % charset.length];
    }

    return result;
};

export const readFileAsText = async (file: File): Promise<string> => {
    const { promise, resolve, reject } = Promise.withResolvers<string>();

    const reader = new FileReader();
    reader.addEventListener('load', (event) => {
        resolve(event.target?.result as string);
    });
    // TODO: refactor
    // eslint-disable-next-line promise/prefer-await-to-callbacks
    reader.addEventListener('error', (error) => {
        reject(error);
    });
    reader.readAsText(file);

    return promise;
};

const sanitize = (input: string) => {
    const map: Record<string, string> = {
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#x27;'
    };
    const reg = /[<>"']/g;

    return input.replaceAll(reg, (match) => map[match]);
};

export const linkify = (text: string) => {
    const sanitized = sanitize(text);
    return sanitized.replaceAll(
        /((?:http|https):\/\/\S+)/g,
        '<a href="$1" target="_blank">$1</a>'
    );
};

export const isValidUUID = (uuid: string) => {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/.test(
        uuid
    );
};

export const isWindows = () => {
    return navigator.userAgent.includes('Windows');
};

export const extractNumericPrefix = (
    str: string | null | undefined
): number[] => {
    if (!str) {
        return [];
    }

    const match = /^([+-]?\d+(?:[.-]\d+)*)/.exec(str);
    if (!match) {
        return [];
    }

    const parts = match[1].split(/[.-]/);
    const numbers: number[] = [];

    for (const part of parts) {
        const number = Number.parseInt(part, 10);
        if (!Number.isNaN(number)) {
            numbers.push(number);
        }
    }

    return numbers;
};

const compareNumberArrays = (a: number[], b: number[]): number => {
    const maxLength = Math.max(a.length, b.length);

    for (let index = 0; index < maxLength; index += 1) {
        const diff = (a[index] ?? 0) - (b[index] ?? 0);
        if (diff !== 0) {
            return diff;
        }
    }

    return 0;
};

export const sortByNumericPrefix = <T extends string>(items: T[]): T[] => {
    return Array.from(items).sort((a, b) => {
        const partsA = extractNumericPrefix(a);
        const partsB = extractNumericPrefix(b);

        if (partsA.length > 0 && partsB.length > 0) {
            const numericComparison = compareNumberArrays(partsA, partsB);
            return numericComparison === 0
                ? a.localeCompare(b)
                : numericComparison;
        }

        if (partsA.length > 0) {
            return -1;
        }

        if (partsB.length > 0) {
            return 1;
        }

        return a.localeCompare(b);
    });
};

export const sortByNumericPrefixProperty = <T>(
    items: T[],
    propertyKey: keyof T
): T[] => {
    return Array.from(items).sort((a, b) => {
        const valueA = a[propertyKey] as string | null | undefined;
        const valueB = b[propertyKey] as string | null | undefined;

        const partsA = extractNumericPrefix(valueA);
        const partsB = extractNumericPrefix(valueB);

        if (partsA.length > 0 && partsB.length > 0) {
            const numericComparison = compareNumberArrays(partsA, partsB);
            if (numericComparison !== 0) {
                return numericComparison;
            }
            return (valueA ?? '').localeCompare(valueB ?? '');
        }

        if (partsA.length > 0) {
            return -1;
        }

        if (partsB.length > 0) {
            return 1;
        }

        return (valueA ?? '').localeCompare(valueB ?? '');
    });
};
